import { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Divider,
  Stack,
} from '@mui/material';
import {
  Delete,
  CloudUpload,
  Check,
  CameraAlt,
  PhotoLibrary,
} from '@mui/icons-material';
import { storageService } from '@/services/firebase/storage';
import { thinkspacesService } from '@/services/collections/thinkspaces';
import { Timestamp } from 'firebase/firestore';
import type { ImageLibraryItem } from '@/types/thinkspace';
import { useCamera } from '@/hooks/useCamera';
import { PlatformUtils } from '@/utils/platform';
import type { CapturedImage } from '@/services/camera/cameraService';

type ImageLibraryDialogProps = {
  open: boolean;
  onClose: () => void;
  thinkSpaceId: string;
  userId: string;
  imageLibrary: ImageLibraryItem[];
  canEdit: boolean;
  onSelectImage: (image: ImageLibraryItem) => void;
};

export const ImageLibraryDialog = ({
  open,
  onClose,
  thinkSpaceId,
  userId,
  imageLibrary,
  canEdit,
  onSelectImage,
}: ImageLibraryDialogProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<ImageLibraryItem | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Camera hook for native platforms
  const { takePhoto, pickFromGallery, loading: cameraLoading, error: cameraError } = useCamera();
  const isNative = PlatformUtils.isNative();

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setError(null);
      setUploading(true);
      setUploadProgress(0);

      try {
        // Get image dimensions
        const dimensions = await getImageDimensions(file);

        // Generate storage path
        const path = `thinkspaces/${thinkSpaceId}/images/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;

        // Upload file
        const imageUrl = await storageService.uploadFile(path, file, (progress) => {
          setUploadProgress(progress);
        });

        // Create library item
        const libraryItem: ImageLibraryItem = {
          id: crypto.randomUUID(),
          imageUrl,
          imagePath: path,
          thumbnailUrl: imageUrl, // Using same URL for now (could generate thumbnail)
          fileName: file.name,
          fileSize: file.size,
          width: dimensions.width,
          height: dimensions.height,
          uploadedAt: Timestamp.now(),
          uploadedBy: userId,
        };

        // Add to ThinkSpace library
        await thinkspacesService.addImageToLibrary(thinkSpaceId, libraryItem);

        // Switch to library tab
        setActiveTab(0);
      } catch (err) {
        console.error('Upload error:', err);
        setError('Failed to upload image. Please try again.');
      } finally {
        setUploading(false);
        setUploadProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [thinkSpaceId, userId]
  );

  // Handler for camera capture (native platforms)
  const handleCameraCapture = useCallback(
    async (capturedImage: CapturedImage | null) => {
      if (!capturedImage) return;

      // Validate file size (max 5MB)
      if (capturedImage.file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setError(null);
      setUploading(true);
      setUploadProgress(0);

      try {
        // Generate storage path
        const path = `thinkspaces/${thinkSpaceId}/images/${Date.now()}_camera.${capturedImage.format}`;

        // Upload file
        const imageUrl = await storageService.uploadFile(path, capturedImage.file, (progress) => {
          setUploadProgress(progress);
        });

        // Create library item (dimensions already extracted by camera service)
        const libraryItem: ImageLibraryItem = {
          id: crypto.randomUUID(),
          imageUrl,
          imagePath: path,
          thumbnailUrl: imageUrl,
          fileName: capturedImage.file.name,
          fileSize: capturedImage.file.size,
          width: capturedImage.width,
          height: capturedImage.height,
          uploadedAt: Timestamp.now(),
          uploadedBy: userId,
        };

        // Add to ThinkSpace library
        await thinkspacesService.addImageToLibrary(thinkSpaceId, libraryItem);

        // Switch to library tab
        setActiveTab(0);
      } catch (err) {
        console.error('Upload error:', err);
        setError('Failed to upload image. Please try again.');
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [thinkSpaceId, userId]
  );

  const handleTakePhoto = useCallback(async () => {
    const image = await takePhoto();
    await handleCameraCapture(image);
  }, [takePhoto, handleCameraCapture]);

  const handlePickFromGallery = useCallback(async () => {
    const image = await pickFromGallery();
    await handleCameraCapture(image);
  }, [pickFromGallery, handleCameraCapture]);

  const handleDeleteImage = useCallback(
    async (image: ImageLibraryItem) => {
      if (!confirm('Delete this image from the library?')) return;

      setDeleting(image.id);
      try {
        // Delete from storage
        await storageService.deleteFile(image.imagePath);

        // Remove from ThinkSpace library
        await thinkspacesService.removeImageFromLibrary(thinkSpaceId, image.id);

        if (selectedImage?.id === image.id) {
          setSelectedImage(null);
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete image');
      } finally {
        setDeleting(null);
      }
    },
    [thinkSpaceId, selectedImage]
  );

  const handleInsert = useCallback(() => {
    if (selectedImage) {
      onSelectImage(selectedImage);
      onClose();
    }
  }, [selectedImage, onSelectImage, onClose]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Image Library</DialogTitle>
      <DialogContent>
        {(error || cameraError) && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error || cameraError?.message}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Library (${imageLibrary.length})`} />
          {canEdit && <Tab label="Upload" />}
        </Tabs>

        {/* Library Tab */}
        {activeTab === 0 && (
          <Box sx={{ minHeight: 300 }}>
            {imageLibrary.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No images in library yet.
                  {canEdit && ' Upload an image to get started.'}
                </Typography>
              </Box>
            ) : (
              <ImageList cols={4} gap={8}>
                {imageLibrary.map((image) => (
                  <ImageListItem
                    key={image.id}
                    onClick={() => setSelectedImage(image)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedImage?.id === image.id ? '3px solid #0f2ccf' : '3px solid transparent',
                      borderRadius: 1,
                      overflow: 'hidden',
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                  >
                    <img
                      src={image.thumbnailUrl}
                      alt={image.fileName}
                      loading="lazy"
                      style={{ height: 120, objectFit: 'cover' }}
                    />
                    <ImageListItemBar
                      title={image.fileName}
                      subtitle={`${image.width}x${image.height} - ${formatFileSize(image.fileSize)}`}
                      actionIcon={
                        canEdit && (
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image);
                            }}
                            disabled={deleting === image.id}
                          >
                            {deleting === image.id ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <Delete />
                            )}
                          </IconButton>
                        )
                      }
                    />
                    {selectedImage?.id === image.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: '#0f2ccf',
                          borderRadius: '50%',
                          p: 0.5,
                        }}
                      >
                        <Check sx={{ color: 'white', fontSize: 16 }} />
                      </Box>
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        )}

        {/* Upload Tab */}
        {activeTab === 1 && canEdit && (
          <Box
            sx={{
              minHeight: 300,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
            }}
          >
            {uploading || cameraLoading ? (
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {cameraLoading ? 'Processing...' : 'Uploading...'}
                </Typography>
                {uploading && (
                  <>
                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ width: '100%', maxWidth: 300, mx: 'auto' }} />
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(uploadProgress)}%
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Stack spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: 300 }}>
                {/* Camera options - only on native platforms */}
                {isNative && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<CameraAlt />}
                      onClick={handleTakePhoto}
                      fullWidth
                      size="large"
                    >
                      Take Photo
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoLibrary />}
                      onClick={handlePickFromGallery}
                      fullWidth
                      size="large"
                    >
                      Choose from Gallery
                    </Button>
                    <Divider sx={{ width: '100%', my: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        or
                      </Typography>
                    </Divider>
                  </>
                )}

                {/* File upload - always available */}
                {!isNative && (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      Click to upload an image
                    </Typography>
                  </>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Supports JPG, PNG, GIF, WebP (max 5MB)
                </Typography>
                <Button
                  variant={isNative ? 'text' : 'contained'}
                  component="label"
                  startIcon={<CloudUpload />}
                  fullWidth={isNative}
                >
                  {isNative ? 'Upload from Files' : 'Choose File'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileSelect}
                  />
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleInsert}
          disabled={!selectedImage}
        >
          Insert Image
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function to get image dimensions
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}
